import apiService from './api';

class AffiliateService {

  // ------------------------------------------------------------------ //
  //  Creator endpoints
  // ------------------------------------------------------------------ //

  async getEarningsSummary() {
    try {
      const data = await apiService.get('/api/affiliate/earnings/summary', {
        cacheTimeout: 60000
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getEarningsBreakdown(groupBy = 'bag') {
    try {
      const data = await apiService.get(`/api/affiliate/earnings/breakdown?group_by=${groupBy}`, {
        cacheTimeout: 60000
      });
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  async getConversions(status = null, limit = 50, offset = 0) {
    try {
      let endpoint = `/api/affiliate/earnings/conversions?limit=${limit}&offset=${offset}`;
      if (status) endpoint += `&status=${status}`;
      const data = await apiService.get(endpoint, { cacheTimeout: 30000 });
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  // ------------------------------------------------------------------ //
  //  Admin endpoints
  // ------------------------------------------------------------------ //

  async getAdminOverview() {
    try {
      const data = await apiService.get('/api/affiliate/admin/overview', {
        cacheTimeout: 30000
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAdminConversions(status = null, limit = 50, offset = 0) {
    try {
      let endpoint = `/api/affiliate/admin/conversions?limit=${limit}&offset=${offset}`;
      if (status) endpoint += `&status=${status}`;
      const data = await apiService.get(endpoint);
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  async getAdminConfigs() {
    try {
      const data = await apiService.get('/api/affiliate/admin/config', {
        cacheTimeout: 120000
      });
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  async updateAdminConfig(configId, configData) {
    try {
      const data = await apiService.put(`/api/affiliate/admin/config/${configId}`, configData);
      apiService.clearCache('GET:/api/affiliate/admin/config');
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async updateConversionStatus(conversionId, status, notes = null) {
    try {
      const body = { status };
      if (notes) body.notes = notes;
      const data = await apiService.put(`/api/affiliate/admin/conversions/${conversionId}`, body);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async batchUpdateConversions(conversionIds, status, notes = null) {
    try {
      const body = { conversion_ids: conversionIds, status };
      if (notes) body.notes = notes;
      const data = await apiService.put('/api/affiliate/admin/conversions/batch', body);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAdminPendingPayouts() {
    try {
      const data = await apiService.get('/api/affiliate/admin/payouts');
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  async updatePayoutStatus(earningsId, payoutStatus, payoutReference = null) {
    try {
      const body = { payout_status: payoutStatus };
      if (payoutReference) body.payout_reference = payoutReference;
      const data = await apiService.put(`/api/affiliate/admin/payouts/${earningsId}`, body);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ------------------------------------------------------------------ //
  //  Activity event tracking (fire-and-forget)
  // ------------------------------------------------------------------ //

  logEvent(eventType, entityType, entityId, bagId = null, metadata = {}) {
    apiService.post('/api/engagement/log', {
      event_type: eventType,
      entity_type: entityType,
      entity_id: entityId,
      bag_id: bagId,
      metadata,
    }).catch(() => {});
  }

  trackBagView(bagId) {
    const key = `gobag_viewed_${bagId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    this.logEvent('bag_view', 'bag', bagId, bagId);
  }

  trackProductClick(itemId, bagId, retailer) {
    this.logEvent('product_click', 'item', itemId, bagId, { retailer });
  }

  trackBagSignup(bagId, source) {
    this.logEvent('bag_signup', 'bag', bagId, bagId, { source });
  }

  trackBagCopy(bagId, source) {
    this.logEvent('bag_copy', 'bag', bagId, bagId, { source });
  }

  trackUserSignup(userId) {
    this.logEvent('user_signup', 'user', userId, null, {});
  }

  // ------------------------------------------------------------------ //
  //  Activity stats endpoints
  // ------------------------------------------------------------------ //

  async getCreatorActivityStats() {
    try {
      const data = await apiService.get('/api/engagement/creator/summary', {
        cacheTimeout: 60000,
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAdminActivityStats() {
    try {
      const data = await apiService.get('/api/engagement/admin/summary', {
        cacheTimeout: 30000,
      });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new AffiliateService();
